import { CreateEventrDto, spErrorTags } from '@eventr/common'
import { FormInputText, MainButton, Typography } from '@eventr/common-storybook'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack } from '@mui/material'
import { useSignalEffect } from '@preact/signals-react'
import { jwtDecode } from 'jwt-decode'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { AdminAPI } from '../../../data/adminCC'
import i18n from '../../../i18n/i18next'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { getNotParsedUrlParam } from '../../../utils'
import { logEvent } from '../../firebase/firebase'
import { useGuestStore } from '../../guest/guest.store'
import { NavigationView } from '../../layout/NavigationView'
import { useRegStore, useRegistrationContext } from '../registrationProvider'
const trimString = (u: unknown) => (typeof u === 'string' ? u.trim() : u)
const validationSchema = z.object({
    teamName: z.preprocess(
        trimString,
        z
            .string()
            .min(3, { message: 'Min 3 characters' })
            .max(25, { message: 'Max 25 characters' })
    ),
})
type ValidationSchema = z.infer<typeof validationSchema>
export const TeamNamePage = () => {
    const inviteUserData = useRegStore((s) => s.inviteUserData)
    const thisUser: string = getNotParsedUrlParam('thisUser')
    const { setCurrentUserAsInviteUser } = useRegistrationContext()

    useSignalEffect(() => {
        if (thisUser) {
            setCurrentUserAsInviteUser()
        }
    })

    const methods = useForm<ValidationSchema>({
        resolver: zodResolver(validationSchema),
    })
    const { handleSubmit, control, setError } = methods

    const onSubmit: SubmitHandler<ValidationSchema> = async (data) => {
        try {
            if (!data.teamName || data.teamName === '') {
                setError('teamName', {
                    message: i18n.t(
                        'authentication.new-team.team-name-incorrect'
                    ),
                })
                return
            }

            //check if team name exists
            const resp = await AdminAPI.eventrExists(data.teamName)
            if (resp === true) {
                //team name exists set error in zod
                setError('teamName', {
                    message: i18n.t('authentication.new-team.team-name-exists'),
                })

                return
            }
            let itineraryCode = null
            if (useGuestStore.getState().cloneOnSignUp) {
                const code = useGuestStore.getState().guestCode
                //base64 decode the code. atob is deprecated but window.atob is not
                itineraryCode = window.atob(code).split('_')[0]
            }

            //team name does not exist lets create it
            const eventr: CreateEventrDto = {
                friendlyName: data.teamName,
                itineraryToCopy: itineraryCode ?? undefined,
                user: {
                    email: (inviteUserData?.email ?? '').toLowerCase(),
                    firstName: inviteUserData?.firstName ?? '',
                    lastName: inviteUserData?.lastName ?? '',
                    password: inviteUserData?.password ?? '',
                    phoneNumber: inviteUserData?.phoneNumber,
                    termsAccepted: true,
                    marketingOptIn: inviteUserData.marketingOptIn,
                    profilePicFile: inviteUserData?.profilePicFile,
                    countryCode: inviteUserData?.countryCode,
                    globalId:
                        inviteUserData?.authTokens !== undefined
                            ? jwtDecode(inviteUserData.authTokens.IdToken).sub
                            : undefined,
                },
            }
            AdminAPI.createEventrAndAdmin({ eventr })
            logEvent('sign_up_create_team', {
                guestCopy: useGuestStore.getState().cloneOnSignUp,
            })
            useGuestStore.setState(useGuestStore.getInitialState())
            window.pushPath('/authentication/setupComplete')
        } catch (e) {
            logEvent('sign_up_create_team_error', {
                message: e instanceof Error ? e.message : 'Unknown error',
            })
            spErrorTags('Error in sign up create team', ['SIGN_UP', 'ERROR'], e)
            defaultAddSnack(i18n.t('snack.network-error'), 'error')
        }
    }

    const backButtonAction = () => {
        window.goBack()
    }

    const body = () => {
        return (
            <FormProvider {...methods}>
                <Stack padding="16px" gap="8px">
                    <Typography>
                        {i18n.t('authentication.new-team.name-explain')}
                    </Typography>
                    <Box padding={'16px 0 0 0'} />
                    <FormInputText
                        id="teamNameField"
                        required
                        fieldLabel={i18n.t('authentication.new-team.team-name')}
                        control={control as any}
                        name="teamName"
                    />
                </Stack>
            </FormProvider>
        )
    }
    const footer = () => {
        return (
            <Stack padding="16px">
                <MainButton
                    id="nextButton"
                    style={{ width: '100%' }}
                    onClick={() => handleSubmit(onSubmit)()}
                    text={i18n.t('general.next')}
                />
            </Stack>
        )
    }

    return (
        <>
            <NavigationView
                backButtonAction={backButtonAction}
                navigationBarTitle={i18n.t('authentication.new-team.team-name')}
                navigationBarSubtitle={i18n.t(
                    'authentication.new-team.enter-team-name'
                )}
                navigationBarDisplayMode="large"
                footer={footer()}
                usePageTransition
            >
                {body()}
            </NavigationView>
        </>
    )
}
